import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CustomerArea, CustomerCompany, Plan, SignupApiService, apiError } from '../signup/signup-api.service';

type Stage = 'loading' | 'login' | 'overview';

// ncommerce.app/cliente: the owner signs in (same account as the signup) and
// sees each establishment with its plan, and can change it. A paid plan is
// charged only after the store is installed with the remote access on; the
// master then opens the Asaas subscription and its invoice shows up here. The
// owner can also turn on the automatic card debit (Asaas's recurring checkout);
// back from it (?cartao=1) the page checks a few times until the card is in.
@Component({
    selector: 'app-customer-area',
    templateUrl: './customer-area.component.html',
    styleUrls: ['../signup/signup.scss', './customer-area.component.scss'],
    standalone: false
})
export class CustomerAreaComponent implements OnInit, OnDestroy {
  stage: Stage = 'loading';
  loading = false;
  error = '';
  notice = '';

  email = '';
  password = '';

  area: CustomerArea | null = null;
  // establishment whose plan is being changed, and the plan picked for it
  editing = '';
  selectedPlan = '';

  // back from the card checkout: how many more checks until we stop waiting
  private cardChecks = 0;
  private cardTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private api: SignupApiService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('cartao')) {
      this.cardChecks = 6;
      this.notice = 'Confirmando o cartão com o Asaas...';
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
    }
    if (this.api.hasAuth()) {
      this.load();
    } else {
      this.stage = 'login';
    }
  }

  signIn(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.error = '';
    this.api.signIn(this.email, this.password).subscribe({
      next: () => this.load(),
      error: err => {
        this.loading = false;
        this.error = err.status === 401 ? 'E-mail ou senha incorretos.' : apiError(err);
      }
    });
  }

  googleSignIn(credential: string): void {
    this.loading = true;
    this.error = '';
    this.api.googleSignIn(credential).subscribe({
      next: () => this.load(),
      error: err => {
        this.loading = false;
        this.error = apiError(err);
      }
    });
  }

  signOut(): void {
    this.api.signOut();
    this.area = null;
    this.password = '';
    this.stage = 'login';
  }

  load(): void {
    this.loading = true;
    this.api.customerArea().subscribe({
      next: area => {
        this.loading = false;
        this.area = area;
        this.stage = 'overview';
        this.waitForCard();
      },
      error: err => {
        this.loading = false;
        if (err.status === 401) {
          this.signOut();
          return;
        }
        this.error = apiError(err);
        this.stage = 'login';
      }
    });
  }

  startChange(company: CustomerCompany): void {
    this.editing = company.name;
    this.selectedPlan = company.plan || '';
    this.error = '';
    this.notice = '';
  }

  cancelChange(): void {
    this.editing = '';
    this.error = '';
  }

  confirmChange(company: CustomerCompany): void {
    if (this.loading || !this.selectedPlan || this.selectedPlan === company.plan) {
      return;
    }
    this.loading = true;
    this.error = '';
    const plan = this.plan(this.selectedPlan);
    this.api.changePlan(company.name, this.selectedPlan).subscribe({
      next: area => {
        this.loading = false;
        this.area = area;
        this.editing = '';
        this.notice = plan && plan.price > 0
          ? `Plano de ${company.nome_fantasia} mudado para ${plan.name}. ` +
            (company.installed ? 'A fatura com o novo valor aparece aqui e no seu e-mail e WhatsApp.'
                               : 'Mandamos o link de pagamento quando o sistema estiver instalado.')
          : `Plano de ${company.nome_fantasia} mudado para ${plan ? plan.name : this.selectedPlan}.`;
      },
      error: err => {
        this.loading = false;
        this.error = apiError(err);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.cardTimer) {
      clearTimeout(this.cardTimer);
    }
  }

  cardAutopay(company: CustomerCompany): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.error = '';
    this.api.cardCheckout(company.name).subscribe({
      next: res => window.location.href = res.link,
      error: err => {
        this.loading = false;
        this.notice = '';
        this.error = apiError(err);
      }
    });
  }

  // Asaas may take a moment to create the card subscription after the checkout.
  private waitForCard(): void {
    if (this.cardChecks <= 0 || !this.area) {
      return;
    }
    if (this.area.companies.some(c => c.card_autopay)) {
      this.cardChecks = 0;
      this.notice = 'Débito automático no cartão ativado!';
      return;
    }
    this.cardChecks--;
    if (this.cardChecks === 0) {
      this.notice = 'Ainda não recebemos a confirmação do cartão. Atualize a página em alguns minutos.';
      return;
    }
    this.cardTimer = setTimeout(() => this.load(), 5000);
  }

  plan(key: string | null | undefined): Plan | undefined {
    return this.area ? this.area.plans.find(p => p.key === key) : undefined;
  }
}
